// store/commentsCacheStore.ts
import { create } from 'zustand'

/** Server shape for a comment (matches the payload you sent) */
export interface CommentUser {
  id: string | null
  username?: string
  display_name?: string
  avatar?: { id?: string; url?: string } | null
}

export interface CommentRow {
  id: string
  parent_id: string | null
  entity_type?: 'App\\Models\\Post'
  entity_id: string
  user_id: string | null
  post_id: string
  model_id?: string | null
  comment: string
  created_at: string
  updated_at: string
  user: CommentUser
  comment_count: number
  like_count: number
  is_liked: boolean
  comments: CommentRow[]
  /** local-only flags */
  __optimistic__?: boolean
  __pending__?: boolean
}

export interface PostCommentsState {
  order: 'asc' | 'desc'
  items: CommentRow[]
  hasMore: boolean
  currentPage: number
  lastPage: number
}
function removeCommentDeep(items: CommentRow[], commentId: string): CommentRow[] {
  return items
    .filter(item => String(item.id) !== String(commentId))
    .map(item => ({
      ...item,
      comments: item.comments?.length
        ? removeCommentDeep(item.comments, commentId)
        : [],
    }))
}
type Store = {
  byPostId: Record<string, PostCommentsState>
  // READ
  getOrdered: (postId: string) => CommentRow[]
  // WRITE
  hydratePage: (postId: string, page: CommentRow[], pageNum: number, lastPage: number) => void
  setOrder: (postId: string, order: 'asc' | 'desc') => void
  setHasMore: (postId: string, hasMore: boolean) => void
  // Optimistic top-level comment
  appendOptimistic: (postId: string, row: CommentRow) => void
  resolveOptimistic: (
    postId: string,
    tempId: string,
    realRow: CommentRow
  ) => void

  rollbackOptimistic: (
    postId: string,
    tempId: string
  ) => void
  // Replies
appendReplyOptimistic: (
  postId: string,
  parentId: string,
  row: CommentRow
) => void

resolveReply: (
  postId: string,
  parentId: string,
  tempId: string,
  realRow: CommentRow
) => void

rollbackReply: (
  postId: string,
  parentId: string,
  tempId: string
) => void
  removeReply: (
  postId: string,
  parentId: string,
  commentId: string
) => void
  // Delete (top-level)
  removeComment: (
    postId: string,
    commentId: string
  ) => void
  // Utilities
  clear: (postId?: string | null) => void
}

const key = (id: string) => String(id)

function sortAsc(a: CommentRow, b: CommentRow) {
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
}

export const useCommentsCacheStore = create<Store>()((set, get) => ({
  byPostId: {},

  getOrdered(postId) {
    const s = get().byPostId[key(postId)]
    if (!s) return []
    const arr = [...s.items]
    return s.order === 'asc' ? arr : arr.slice().reverse()
  },

  hydratePage(postId, page, pageNum, lastPage) {
    set(state => {
      const k = key(postId)
      const existing = state.byPostId[k] ?? {
        order: 'asc' as const,
        items: [],
        hasMore: false,
        currentPage: 0,
        lastPage: 1,
      }

      const map = new Map<string, CommentRow>()
      for (const c of existing.items) map.set(c.id, c)
      for (const c of page) map.set(c.id, c)

      const merged = Array.from(map.values()).sort(sortAsc)

      const nextCurrent = Math.max(existing.currentPage, pageNum) // don’t regress
      const nextLast = Math.max(existing.lastPage, lastPage ?? existing.lastPage)
      const nextHasMore = nextCurrent < nextLast

      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...existing,
            items: merged,
            currentPage: nextCurrent,
            lastPage: nextLast,
            hasMore: nextHasMore,
          },
        },
      }
    })
  },

  setOrder(postId, order) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: { ...s, order },
        },
      }
    })
  },

  setHasMore(postId, hasMore) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: { ...s, hasMore },
        },
      }
    })
  },

  appendOptimistic(postId, row) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k] ?? {
        order: 'asc' as const,
        items: [],
        hasMore: false,
        currentPage: 1,
        lastPage: 1,
      }
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: [...s.items, { ...row, __optimistic__: true, __pending__: true }],
          },
        },
      }
    })
  },

  resolveOptimistic(postId, tempId, realRow) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.map(it => (it.id === tempId ? { ...realRow } : it)),
          },
        },
      }
    })
  },

  rollbackOptimistic(postId, tempId) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.filter(it => it.id !== tempId),
          },
        },
      }
    })
  },

  appendReplyOptimistic(postId, parentId, row) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.map(it =>
              it.id === parentId
                ? {
                    ...it,
                    comments: [...(it.comments ?? []), { ...row, __optimistic__: true, __pending__: true }].sort(
                      sortAsc
                    ),
                  }
                : it
            ),
          },
        },
      }
    })
  },

  resolveReply(postId, parentId, tempId, realRow) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.map(it =>
              it.id === parentId
                ? {
                    ...it,
                    comments: (it.comments ?? []).map(r => (r.id === tempId ? { ...realRow } : r)),
                  }
                : it
            ),
          },
        },
      }
    })
  },

  rollbackReply(postId, parentId, tempId) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state
      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.map(it =>
              it.id === parentId
                ? { ...it, comments: (it.comments ?? []).filter(r => r.id !== tempId) }
                : it
            ),
          },
        },
      }
    })
  },
  removeReply(postId, parentId, commentId) {
    set(state => {
      const k = key(postId)
      const s = state.byPostId[k]
      if (!s) return state

      return {
        ...state,
        byPostId: {
          ...state.byPostId,
          [k]: {
            ...s,
            items: s.items.map(it =>
              String(it.id) === String(parentId)
                ? {
                    ...it,
                    comments: (it.comments ?? []).filter(
                      reply => String(reply.id) !== String(commentId)
                    ),
                    comment_count: Math.max(0, (it.comment_count ?? 0) - 1),
                  }
                : it
            ),
          },
        },
      }
    })
  },
removeComment(postId, commentId) {
  set(state => {
    const k = key(postId)
    const s = state.byPostId[k]
    if (!s) return state

    return {
      ...state,
      byPostId: {
        ...state.byPostId,
        [k]: {
          ...s,
          items: removeCommentDeep(s.items, commentId),
        },
      },
    }
  })
},
  clear(postId) {
    if (postId == null) {
      set({ byPostId: {} })
    } else {
      set(state => {
        const copy = { ...state.byPostId }
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete copy[key(postId)]
        return { ...state, byPostId: copy }
      })
    }
  },
}))
